import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import ClienteAutocomplete from '../components/ClienteAutocomplete';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon, Save, RefreshCw, Download, Upload, FileSpreadsheet, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { formatPaymentName, getTotalPaymentsFromType, extractYearFromDate, generateYearOptions } from '../utils/helpers';
import { MentionSuggestions, CloneModal, BulkUploadResultsDialog, DuplicateWarningDialog, TemplateDownloadDialog, SalidaCamaraSection } from '../components/entrada';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const EntradaDatos = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const editSample = location.state?.editSample;
  const dashboardFilters = location.state?.dashboardFilters;
  const returnTo = location.state?.returnTo || 'dashboard';
  const isEditing = !!editSample;

  const [destinations, setDestinations] = useState([]);
  const [ensayos, setEnsayos] = useState([]);
  const [estados, setEstados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Bulk upload results dialog
  const [bulkUploadResults, setBulkUploadResults] = useState({
    open: false,
    samplesCreated: 0,
    errors: [],
  });

  // Template download dialog
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);

  const [formData, setFormData] = useState({
    destination: '',
    client: '',
    product: '',
    // Campos ANMAR
    ensayo: '',
    subcategory: '',
    price: '',
    num_anmar: '',
    num_challenge: '',
    fecha_ejecucion: '',
    fecha_informe: '',
    documentacion_enviada: '',
    otros_ensayos: '',
    resultado_otros_ensayos: '',
    // Campos comunes
    num_ensayo: '',
    fecha_entrada: '',
    fecha_envio: '',
    idioma: [],
    observaciones_incidencias: '',
    // Campos GAIKER/IVAMI/REGULATORY
    coste_goya: '',
    num_presupuesto_goya: '',
    num_presupuesto_gaiker: '',
    num_proforma_ivami: '',
    fecha_envio_informe: '',
    ensayo_ivami: 'HETCAM',  // Por defecto para IVAMI
    // Hitos de facturación
    payment_total: '',
    payment_type: 'UNICO',
    // ESTABILIDADES
    fechas_salida_camara: [{ numero: 1, fecha: '', metodo: '' }],
    fecha_informe_estabilidades: '',
    // Estado
    status_id: [],
  });

  // Payment state
  const [payments, setPayments] = useState([]);

  // Format number with thousand separator (punto) - Spanish format
  const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    
    // Handle string inputs - remove any existing formatting
    let num = price;
    if (typeof price === 'string') {
      // Remove dots (thousand separators) and replace comma with dot for parsing
      num = parseFloat(price.replace(/\./g, '').replace(',', '.'));
    }
    
    if (isNaN(num)) return '';
    
    // Format with Spanish locale: punto for thousands, coma for decimals
    // Use useGrouping: true to ensure thousand separators are always applied
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    }).format(num);
  };

  // Track if payments have been loaded from edit sample (to avoid recalculating)
  const [paymentsLoadedFromEdit, setPaymentsLoadedFromEdit] = useState(false);

  // Calculate payments when price or payment type changes (only for new samples or manual changes)
  useEffect(() => {
    // Don't recalculate if we're editing and haven't finished loading
    if (isEditing && payments.length === 0 && !paymentsLoadedFromEdit) {
      return;
    }
    if (formData.payment_total && ['ANMAR', 'GAIKER', 'IVAMI', 'REGULATORY', 'ESTABILIDADES'].includes(formData.destination)) {
      calculatePayments();
    }
  }, [formData.payment_total, formData.payment_type, formData.destination]);

  const calculatePayments = () => {
    // If payments were loaded from edit, don't overwrite them unless user changed something
    if (paymentsLoadedFromEdit && payments.length > 0) {
      // Check if payment_type changed - only then recalculate
      const currentType = formData.payment_type;
      const expectedCount = currentType === 'UNICO' ? 1 : currentType === 'DOS_PAGOS' ? 2 : currentType === 'TRES_PAGOS' ? 3 : 4;
      if (payments.length === expectedCount) {
        // Just update amounts, keep paid status
        const total = parseFloat(formData.payment_total);
        if (!isNaN(total) && total > 0) {
          // Round to 2 decimal places to avoid floating point errors
          const amountPerPayment = Math.round((total / expectedCount) * 100) / 100;
          const updatedPayments = payments.map((p, idx) => ({
            ...p,
            amount: amountPerPayment
          }));
          setPayments(updatedPayments);
        }
        return;
      }
    }
    
    const total = parseFloat(formData.payment_total);
    if (isNaN(total) || total <= 0) {
      setPayments([]);
      return;
    }

    let newPayments = [];
    
    if (formData.payment_type === 'UNICO') {
      // Round to 2 decimal places
      const amount = Math.round(total * 100) / 100;
      newPayments = [{ payment_number: 1, amount: amount, paid: false, billing_status: 'Pte facturar' }];
    } else if (formData.payment_type === 'DOS_PAGOS') {
      const half = Math.round((total / 2) * 100) / 100;
      newPayments = [
        { payment_number: 1, amount: half, paid: false, billing_status: 'Pte facturar' },
        { payment_number: 2, amount: half, paid: false, billing_status: 'Pte facturar' }
      ];
    } else if (formData.payment_type === 'TRES_PAGOS') {
      const first = Math.round((total * 0.50) * 100) / 100;
      const second = Math.round((total * 0.25) * 100) / 100;
      const third = Math.round((total - first - second) * 100) / 100;
      newPayments = [
        { payment_number: 1, amount: first, paid: false, billing_status: 'Pte facturar' },
        { payment_number: 2, amount: second, paid: false, billing_status: 'Pte facturar' },
        { payment_number: 3, amount: third, paid: false, billing_status: 'Pte facturar' }
      ];
    } else if (formData.payment_type === 'CUATRO_PAGOS') {
      const quarter = Math.round((total / 4) * 100) / 100;
      newPayments = [
        { payment_number: 1, amount: quarter, paid: false, billing_status: 'Pte facturar' },
        { payment_number: 2, amount: quarter, paid: false, billing_status: 'Pte facturar' },
        { payment_number: 3, amount: quarter, paid: false, billing_status: 'Pte facturar' },
        { payment_number: 4, amount: quarter, paid: false, billing_status: 'Pte facturar' }
      ];
    }

    setPayments(newPayments);
  };

  const togglePaymentPaid = (index) => {
    const updated = [...payments];
    updated[index].paid = !updated[index].paid;
    // When marking as paid, set default values
    if (updated[index].paid) {
      // Set billing_status to "Pte facturar" by default
      if (!updated[index].billing_status) {
        updated[index].billing_status = 'Pte facturar';
      }
      // Set payment_year to baseYear if not already set
      if (!updated[index].payment_year) {
        const baseYear = extractYearFromDate(formData.fecha_entrada) || String(new Date().getFullYear());
        updated[index].payment_year = baseYear;
      }
    }
    setPayments(updated);
  };

  const updatePaymentBillingStatus = (index, billingStatus) => {
    const updated = [...payments];
    updated[index].billing_status = billingStatus;
    setPayments(updated);
  };

  const [selectedEnsayo, setSelectedEnsayo] = useState(null);
  const [dates, setDates] = useState({
    fecha_entrada: null,
    fecha_envio: null,
    fecha_ejecucion: null,
    fecha_informe: null,
    fecha_envio_informe: null,
  });

  // Clone modal state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneQuantity, setCloneQuantity] = useState(1);
  const [cloningInProgress, setCloningInProgress] = useState(false);

  // Clone after create state (for new samples)
  const [showCloneAfterCreateModal, setShowCloneAfterCreateModal] = useState(false);
  const [newlyCreatedSampleId, setNewlyCreatedSampleId] = useState(null);

  // Duplicate detection state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [pendingSaveData, setPendingSaveData] = useState(null);

  // Mentions autocomplete state
  const [users, setUsers] = useState([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = React.useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Reload clientes and estados when destination changes
  useEffect(() => {
    if (formData.destination) {
      loadClientesYEstados();
    }
  }, [formData.destination]);

  const loadClientesYEstados = async () => {
    try {
      const [clientesRes, estadosRes] = await Promise.all([
        axios.get(`/clientes?destination=${formData.destination}`),
        axios.get(`/estados?destination=${formData.destination}`)
      ]);
      setClientes(clientesRes.data);
      setEstados(estadosRes.data);
    } catch (error) {
      console.error('Error loading clientes/estados:', error);
    }
  };

  useEffect(() => {
    if (formData.ensayo) {
      const ensayo = ensayos.find((e) => e.name === formData.ensayo);
      setSelectedEnsayo(ensayo);
      if (ensayo && !ensayo.subcategories.includes(formData.subcategory)) {
        setFormData({ ...formData, subcategory: '' });
      }
    }
  }, [formData.ensayo, ensayos]);

  // Set default "HETCAM" when IVAMI destination is selected
  useEffect(() => {
    if (formData.destination === 'IVAMI' && !formData.ensayo && ensayos.length > 0) {
      const hetcamEnsayo = ensayos.find((e) => e.name === 'HETCAM');
      if (hetcamEnsayo) {
        setFormData({ ...formData, ensayo: 'HETCAM' });
      }
    }
  }, [formData.destination, ensayos]);

  const fetchData = async () => {
    try {
      const [destRes, ensayosRes, estadosRes, clientesRes] = await Promise.all([
        axios.get('/destinations'),
        axios.get('/ensayos'),
        axios.get('/estados'),
        axios.get('/clientes'),
      ]);

      setDestinations(destRes.data);
      setEnsayos(ensayosRes.data);
      setEstados(estadosRes.data);
      setClientes(clientesRes.data);

      // Load users for @mentions - accessible to all authenticated users
      try {
        const usersRes = await axios.get('/users/mentions');
        setUsers(usersRes.data);
      } catch {
        // Fallback: mentions won't autocomplete
      }

      // If editing, populate form AFTER destinations are loaded
      if (editSample) {
        console.log('[DEBUG] Editing sample, destination:', editSample.destination);
        
        // Helper function to parse DD/MM/YYYY to Date object
        const parseDate = (dateStr) => {
          if (!dateStr) return null;
          // Handle both DD/MM/YYYY and YYYY-MM-DD formats
          if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
              const year = parseInt(parts[2], 10);
              return new Date(year, month, day);
            }
          } else if (dateStr.includes('-')) {
            const datePart = dateStr.split(' ')[0];
            const parts = datePart.split('-');
            if (parts.length === 3) {
              const year = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const day = parseInt(parts[2], 10);
              return new Date(year, month, day);
            }
          }
          return null;
        };

        // Unificar observaciones e incidencias para compatibilidad con datos antiguos
        const observaciones_incidencias = editSample.observaciones_incidencias || 
          [editSample.observaciones, editSample.incidencias].filter(Boolean).join('\n\n') || '';
        
        // Ensure destination exists in the loaded destinations
        const destinationValue = editSample.destination || '';
        const destinationExists = destRes.data.some(d => d.name === destinationValue);
        
        if (!destinationExists && destinationValue) {
          console.warn('[DEBUG] Destination not found in loaded destinations:', destinationValue);
        }
        
        setFormData({
          destination: destinationValue,
          client: editSample.client || '',
          product: editSample.product || '',
          ensayo: editSample.ensayo || '',
          subcategory: editSample.subcategory || '',
          price: editSample.price ? editSample.price.toString() : '',
          num_ensayo: editSample.num_ensayo || '',
          num_anmar: editSample.num_anmar || '',
          num_challenge: editSample.num_challenge || '',
          fecha_entrada: editSample.fecha_entrada || '',
          fecha_envio: editSample.fecha_envio || '',
          fecha_ejecucion: editSample.fecha_ejecucion || '',
          fecha_informe: editSample.fecha_informe || '',
          documentacion_enviada: editSample.documentacion_enviada || '',
          otros_ensayos: editSample.otros_ensayos || '',
          resultado_otros_ensayos: editSample.resultado_otros_ensayos || '',
          coste_goya: editSample.coste_goya ? editSample.coste_goya.toString() : '',
          num_presupuesto_goya: editSample.num_presupuesto_goya || '',
          num_presupuesto_gaiker: editSample.num_presupuesto_gaiker || '',
          num_proforma_ivami: editSample.num_proforma_ivami || '',
          fecha_envio_informe: editSample.fecha_envio_informe || '',
          ensayo_ivami: editSample.ensayo_ivami || 'HETCAM',
          payment_total: editSample.payment_config?.total_amount ? editSample.payment_config.total_amount.toString() : '',
          payment_type: editSample.payment_config?.payment_type || 'UNICO',
          idioma: Array.isArray(editSample.idioma) ? editSample.idioma : (editSample.idioma ? [editSample.idioma] : []),
          observaciones_incidencias: observaciones_incidencias,
          status_id: Array.isArray(editSample.status_id) ? editSample.status_id : (editSample.status_id ? [editSample.status_id] : []),
          fechas_salida_camara: Array.isArray(editSample.fechas_salida_camara) && editSample.fechas_salida_camara.length > 0
            ? editSample.fechas_salida_camara
            : [{ numero: 1, fecha: '', metodo: '' }],
          fecha_informe_estabilidades: editSample.fecha_informe_estabilidades || '',
        });

        // Load payment config
        if (editSample.payment_config) {
          console.log('[DEBUG] Loading payment_config:', JSON.stringify(editSample.payment_config, null, 2));
          let loadedPayments = editSample.payment_config.payments || [];
          
          // SIMPLIFIED FIX: Use payments directly from backend if they have all required fields
          // Only reconstruct if fields are missing (legacy data)
          const needsReconstruction = loadedPayments.some(p => 
            p.amount === undefined || p.amount === null
          );
          
          if (needsReconstruction && loadedPayments.length > 0) {
            console.log('[DEBUG] Reconstructing payments - some fields are missing');
            const total = editSample.payment_config.total_amount;
            const type = editSample.payment_config.payment_type;
            
            // Preserve original paid/payment_month/billing_status values
            const originalStates = loadedPayments.map(p => ({
              paid: p.paid !== undefined ? p.paid : false,
              payment_month: p.payment_month || null,
              billing_status: p.billing_status || 'Pte facturar'
            }));
            
            let reconstructed = [];
            if (type === 'UNICO') {
              reconstructed = [{ 
                payment_number: 1, 
                amount: total, 
                paid: originalStates[0]?.paid || false,
                payment_month: originalStates[0]?.payment_month || null,
                billing_status: originalStates[0]?.billing_status || 'Pte facturar'
              }];
            } else if (type === 'DOS_PAGOS') {
              const half = Math.round((total / 2) * 100) / 100;
              reconstructed = [
                { payment_number: 1, amount: half, paid: originalStates[0]?.paid || false, payment_month: originalStates[0]?.payment_month || null, billing_status: originalStates[0]?.billing_status || 'Pte facturar' },
                { payment_number: 2, amount: half, paid: originalStates[1]?.paid || false, payment_month: originalStates[1]?.payment_month || null, billing_status: originalStates[1]?.billing_status || 'Pte facturar' }
              ];
            } else if (type === 'TRES_PAGOS') {
              const first = Math.round((total * 0.50) * 100) / 100;
              const second = Math.round((total * 0.25) * 100) / 100;
              const third = Math.round((total - first - second) * 100) / 100;
              reconstructed = [
                { payment_number: 1, amount: first, paid: originalStates[0]?.paid || false, payment_month: originalStates[0]?.payment_month || null, billing_status: originalStates[0]?.billing_status || 'Pte facturar' },
                { payment_number: 2, amount: second, paid: originalStates[1]?.paid || false, payment_month: originalStates[1]?.payment_month || null, billing_status: originalStates[1]?.billing_status || 'Pte facturar' },
                { payment_number: 3, amount: third, paid: originalStates[2]?.paid || false, payment_month: originalStates[2]?.payment_month || null, billing_status: originalStates[2]?.billing_status || 'Pte facturar' }
              ];
            } else if (type === 'CUATRO_PAGOS') {
              const quarter = Math.round((total / 4) * 100) / 100;
              reconstructed = [
                { payment_number: 1, amount: quarter, paid: originalStates[0]?.paid || false, payment_month: originalStates[0]?.payment_month || null, billing_status: originalStates[0]?.billing_status || 'Pte facturar' },
                { payment_number: 2, amount: quarter, paid: originalStates[1]?.paid || false, payment_month: originalStates[1]?.payment_month || null, billing_status: originalStates[1]?.billing_status || 'Pte facturar' },
                { payment_number: 3, amount: quarter, paid: originalStates[2]?.paid || false, payment_month: originalStates[2]?.payment_month || null, billing_status: originalStates[2]?.billing_status || 'Pte facturar' },
                { payment_number: 4, amount: quarter, paid: originalStates[3]?.paid || false, payment_month: originalStates[3]?.payment_month || null, billing_status: originalStates[3]?.billing_status || 'Pte facturar' }
              ];
            }
            loadedPayments = reconstructed;
            console.log('[DEBUG] Payments reconstructed with preserved states');
          } else {
            console.log('[DEBUG] Using payments directly from backend - all fields present');
          }
          
          console.log('[DEBUG] Final payments:', JSON.stringify(loadedPayments, null, 2));
          setPayments(loadedPayments);
          setPaymentsLoadedFromEdit(true);
        } else {
          // No payment_config, allow recalculation
          setPaymentsLoadedFromEdit(false);
        }

        // Parse and set dates
        setDates({
          fecha_entrada: parseDate(editSample.fecha_entrada),
          fecha_envio: parseDate(editSample.fecha_envio),
          fecha_ejecucion: parseDate(editSample.fecha_ejecucion),
          fecha_informe: parseDate(editSample.fecha_informe),
          fecha_envio_informe: parseDate(editSample.fecha_envio_informe),
        });
      } else if (user && !user.is_superuser && user.assigned_destinations.length === 1) {
        // Auto-select destination if user has only one assigned
        setFormData(prev => ({
          ...prev,
          destination: user.assigned_destinations[0]
        }));
      }
    } catch (error) {
      toast.error('Error al cargar los datos');
      console.error(error);
    }
  };

  const checkForDuplicates = async () => {
    try {
      // Only check for new samples (not when editing)
      if (isEditing) return null;

      // Check if required fields are filled
      if (!formData.destination || !formData.client || !formData.product) {
        return null;
      }

      // For ANMAR, also need ensayo
      if (formData.destination === 'ANMAR' && !formData.ensayo) {
        return null;
      }

      const params = new URLSearchParams({
        destination: formData.destination,
        client: formData.client,
        product: formData.product
      });

      // Add ensayo for ANMAR
      if (formData.destination === 'ANMAR' && formData.ensayo) {
        params.append('ensayo', formData.ensayo);
      }

      const response = await axios.post(`/work-samples/check-duplicate?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        price: formData.price ? Math.round(parseFloat(formData.price) * 100) / 100 : null,
        coste_goya: formData.coste_goya ? Math.round(parseFloat(formData.coste_goya) * 100) / 100 : null,
      };

      // Add payment_config for ALL destinations with payment system
      if (['ANMAR', 'GAIKER', 'IVAMI', 'REGULATORY', 'ESTABILIDADES'].includes(formData.destination) && formData.payment_total) {
        const totalAmount = Math.round(parseFloat(formData.payment_total) * 100) / 100;
        payload.payment_config = {
          total_amount: totalAmount,
          payment_type: formData.payment_type,
          payments: payments.map(p => ({
            ...p,
            amount: Math.round(p.amount * 100) / 100
          }))
        };
      }

      // Clean ESTABILIDADES-only fields for other destinations
      if (formData.destination !== 'ESTABILIDADES') {
        delete payload.fechas_salida_camara;
        delete payload.fecha_informe_estabilidades;
      } else {
        // Filter out empty salida camara entries
        if (payload.fechas_salida_camara) {
          payload.fechas_salida_camara = payload.fechas_salida_camara.filter(e => e.fecha);
        }
      }

      // Remove form-only fields not in backend model
      delete payload.payment_total;
      delete payload.payment_type;

      if (isEditing) {
        await axios.put(`/work-samples/${editSample.id}`, payload);
        toast.success('¡Muestra actualizada correctamente!');
        if (returnTo === 'facturacion') {
          navigate('/facturacion');
        } else {
          navigate('/dashboard', { state: { restoreFilters: dashboardFilters } });
        }
      } else {
        // Check for duplicates before creating
        const duplicateCheck = await checkForDuplicates();
        
        if (duplicateCheck && duplicateCheck.exists) {
          // Show confirmation dialog
          setDuplicateInfo(duplicateCheck.sample);
          setPendingSaveData(payload);
          setShowDuplicateDialog(true);
          setLoading(false);
          return; // Don't save yet, wait for user confirmation
        }

        // No duplicates, proceed with save
        const response = await axios.post('/work-samples', payload);
        toast.success('¡Muestra guardada correctamente!');
        
        // Ask if user wants to clone the newly created sample
        if (response.data && response.data.id) {
          setNewlyCreatedSampleId(response.data.id);
          setCloneQuantity(1);
          setShowCloneAfterCreateModal(true);
        } else {
          resetForm();
        }
      }
    } catch (error) {
      toast.error(isEditing ? 'Error al actualizar la muestra' : 'Error al guardar la muestra');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle cloning after creating a new sample
  const handleCloneAfterCreate = async () => {
    setCloningInProgress(true);
    try {
      const response = await axios.post(`/work-samples/${newlyCreatedSampleId}/clone`, null, {
        params: { quantity: cloneQuantity }
      });
      
      toast.success(`${cloneQuantity} ${cloneQuantity === 1 ? 'clon creado' : 'clones creados'} correctamente`);
      setShowCloneAfterCreateModal(false);
      setNewlyCreatedSampleId(null);
      
      // Navigate to the first clone for editing
      if (response.data.cloned_ids && response.data.cloned_ids.length > 0) {
        const firstCloneId = response.data.cloned_ids[0];
        toast.info('Cargando primer clon para editar...');
        setTimeout(() => {
          navigate(`/entrada-datos?edit=${firstCloneId}`);
          window.location.reload();
        }, 500);
      } else {
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al clonar el estudio');
    } finally {
      setCloningInProgress(false);
    }
  };

  const handleSkipCloneAfterCreate = () => {
    setShowCloneAfterCreateModal(false);
    setNewlyCreatedSampleId(null);
    resetForm();
    toast.info('Muestra creada sin clones');
  };

  const handleConfirmDuplicate = async () => {
    setShowDuplicateDialog(false);
    setLoading(true);
    
    try {
      const response = await axios.post('/work-samples', pendingSaveData);
      toast.success('¡Muestra guardada correctamente!');
      setPendingSaveData(null);
      setDuplicateInfo(null);
      
      // Ask if user wants to clone
      if (response.data && response.data.id) {
        setNewlyCreatedSampleId(response.data.id);
        setCloneQuantity(1);
        setShowCloneAfterCreateModal(true);
      } else {
        resetForm();
      }
    } catch (error) {
      toast.error('Error al guardar la muestra');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateDialog(false);
    setPendingSaveData(null);
    setDuplicateInfo(null);
    toast.info('Registro cancelado');
  };

  // Handle mentions autocomplete
  const handleObservacionesChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    setFormData({ ...formData, observaciones_incidencias: value });
    setCursorPosition(cursorPos);
    
    // Check if user just typed @
    const textBeforeCursor = value.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      const filter = mentionMatch[1].toLowerCase();
      setMentionFilter(filter);
      
      const filtered = users.filter(u => 
        u.full_name?.toLowerCase().includes(filter) ||
        u.email?.toLowerCase().includes(filter)
      );
      
      setFilteredUsers(filtered);
      setShowMentionSuggestions(true);
    } else {
      setShowMentionSuggestions(false);
    }
  };

  const insertMention = (user) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const value = formData.observaciones_incidencias;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    
    // Remove the partial @ mention
    const beforeMention = textBeforeCursor.replace(/@\w*$/, '');
    
    // Insert the full mention
    const mention = `@${user.email} `;
    const newValue = beforeMention + mention + textAfterCursor;
    const newCursorPos = beforeMention.length + mention.length;
    
    setFormData({ ...formData, observaciones_incidencias: newValue });
    setShowMentionSuggestions(false);
    
    // Focus and set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const resetForm = () => {
    setFormData({
      destination: '',
      client: '',
      product: '',
      ensayo: '',
      subcategory: '',
      price: '',
      num_anmar: '',
      num_ensayo: '',
      fecha_entrada: '',
      fecha_envio: '',
      fecha_ejecucion: '',
      fecha_informe: '',
      documentacion_enviada: '',
      otros_ensayos: '',
      resultado_otros_ensayos: '',
      coste_goya: '',
      num_presupuesto_goya: '',
      num_presupuesto_gaiker: '',
      num_proforma_ivami: '',
      fecha_envio_informe: '',
      ensayo_ivami: 'HETCAM',
      payment_total: '',
      payment_type: 'UNICO',
      fechas_salida_camara: [{ numero: 1, fecha: '', metodo: '' }],
      fecha_informe_estabilidades: '',
      idioma: [],
      observaciones_incidencias: '',
      status_id: [],
    });
    setPayments([]);
    setDates({
      fecha_entrada: null,
      fecha_envio: null,
      fecha_ejecucion: null,
      fecha_informe: null,
      fecha_envio_informe: null,
    });
    setSelectedEnsayo(null);
  };

  const handleDateSelect = (field, date) => {
    setDates({ ...dates, [field]: date || null });
    setFormData({ ...formData, [field]: date ? format(date, 'dd/MM/yyyy') : '' });
  };

  const clearDate = (field) => {
    setDates({ ...dates, [field]: null });
    setFormData({ ...formData, [field]: '' });
  };

  const handleStatusSelect = (statusId, isChecked) => {
    let newStatusIds;
    if (isChecked) {
      // Add status
      newStatusIds = [...formData.status_id, statusId];
    } else {
      // Remove status
      newStatusIds = formData.status_id.filter(id => id !== statusId);
    }
    setFormData({ ...formData, status_id: newStatusIds });
  };

  const handleDownloadTemplate = async (destination = null) => {
    try {
      // Get the base URL and token
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      
      const url = destination 
        ? `${baseUrl}/api/work-samples/template?destination=${destination}`
        : `${baseUrl}/api/work-samples/template`;
      
      const filename = destination 
        ? `plantilla_${destination}.xlsx`
        : 'plantilla_general.xlsx';
      
      // Use fetch with authorization header
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al descargar');
      }
      
      const blob = await response.blob();
      
      // Try using the download attribute approach
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      
      // For Safari and some browsers, we need to simulate a click differently
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      link.dispatchEvent(clickEvent);
      
      // Cleanup
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      
      toast.success(`Plantilla ${destination || 'general'} descargada`);
      setShowTemplateDialog(false);
    } catch (error) {
      toast.error('Error al descargar la plantilla');
      console.error('Download error:', error);
    }
  };


  const handleCloneStudy = async () => {
    if (!editSample?.id) {
      toast.error('Debes guardar el estudio primero para poder clonarlo');
      return;
    }

    if (!formData.num_ensayo || !formData.num_ensayo.match(/^\d+$/)) {
      toast.error('El Nº GOYA debe ser numérico para poder clonar');
      return;
    }

    setShowCloneModal(true);
  };

  const confirmClone = async () => {
    setCloningInProgress(true);
    try {
      const response = await axios.post(`/work-samples/${editSample.id}/clone`, null, {
        params: { quantity: cloneQuantity }
      });

      toast.success(`${cloneQuantity} ${cloneQuantity === 1 ? 'clon creado' : 'clones creados'} correctamente`);
      setShowCloneModal(false);
      
      // Navigate to the first clone for editing
      if (response.data.cloned_ids && response.data.cloned_ids.length > 0) {
        const firstCloneId = response.data.cloned_ids[0];
        toast.info('Cargando primer clon para editar...');
        setTimeout(() => {
          navigate(`/entrada-datos?edit=${firstCloneId}`);
          window.location.reload(); // Force reload to load the clone
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al clonar el estudio');
    } finally {
      setCloningInProgress(false);
    }
  };

  const getPreviewNumGoya = () => {
    if (!formData.num_ensayo || !formData.num_ensayo.match(/^\d+$/)) {
      return [];
    }
    const baseNumber = parseInt(formData.num_ensayo);
    const length = formData.num_ensayo.length;
    const previews = [];
    for (let i = 1; i <= cloneQuantity; i++) {
      previews.push(String(baseNumber + i).padStart(length, '0'));
    }
    return previews;
  };


  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      toast.error('Por favor selecciona un archivo Excel');
      return;
    }

    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post('/work-samples/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const { samples_created, errors } = response.data;
      
      // Show results dialog
      setBulkUploadResults({
        open: true,
        samplesCreated: samples_created,
        errors: errors || [],
      });
      
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('bulk-upload-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el archivo');
      console.error(error);
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="entrada-datos-container">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Muestra' : 'Introducción de Datos'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEditing ? 'Actualiza los datos de la muestra' : 'Registra una nueva muestra en el sistema'}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Información de la Muestra</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="destination">
                  Destino <span className="text-red-500">*</span>
                  {user && !user.is_superuser && user.assigned_destinations.length === 1 && (
                    <span className="ml-2 text-xs text-gray-500 font-normal">(Asignado automáticamente)</span>
                  )}
                </Label>
                <Select
                  value={formData.destination}
                  onValueChange={(value) =>
                    setFormData({ ...formData, destination: value })
                  }
                  required
                  disabled={user && !user.is_superuser && user.assigned_destinations.length === 1}
                  data-testid="destination-select"
                  key={isEditing ? `edit-${formData.destination}` : 'new'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un destino" />
                  </SelectTrigger>
                  <SelectContent>
                    {destinations.map((dest) => (
                      <SelectItem key={dest.id} value={dest.name}>
                        {dest.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="client">
                  Cliente <span className="text-red-500">*</span>
                </Label>
                <ClienteAutocomplete
                  id="client"
                  clientes={clientes}
                  value={formData.client}
                  onChange={(value) => setFormData({ ...formData, client: value })}
                  placeholder="Escribe para buscar un cliente..."
                  required
                  data-testid="client-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="product">
                  Producto <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="product"
                  value={formData.product}
                  onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                  placeholder="Nombre del producto"
                  required
                  data-testid="product-input"
                />
              </div>

              {/* Campos específicos ANMAR e IVAMI */}
              {(formData.destination === 'ANMAR' || formData.destination === 'IVAMI') && (
                <div className="space-y-2">
                  <Label htmlFor="ensayo">
                    Ensayo <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.ensayo}
                    onValueChange={(value) =>
                      setFormData({ ...formData, ensayo: value })
                    }
                    required
                    data-testid="ensayo-select"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un ensayo" />
                    </SelectTrigger>
                    <SelectContent>
                      {ensayos.map((ens) => (
                        <SelectItem key={ens.id} value={ens.name}>
                          {ens.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Campos específicos ANMAR - Subcategoría */}
            {formData.destination === 'ANMAR' && (
              <div className="space-y-2">
                <Label htmlFor="subcategory">
                  Subcategoría <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.subcategory}
                  onValueChange={(value) =>
                    setFormData({ ...formData, subcategory: value })
                  }
                  required
                  disabled={!selectedEnsayo || selectedEnsayo.subcategories.length === 0}
                  data-testid="subcategory-select"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una subcategoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedEnsayo?.subcategories.map((sub, idx) => (
                      <SelectItem key={idx} value={sub}>
                        {sub}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Numbers */}
            <div className={`grid grid-cols-1 ${formData.destination === 'ANMAR' ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
              <div className="space-y-2">
                <Label htmlFor="num_ensayo">Nº GOYA (máx. 8 caracteres)</Label>
                <Input
                  id="num_ensayo"
                  maxLength={8}
                  value={formData.num_ensayo}
                  onChange={(e) => setFormData({ ...formData, num_ensayo: e.target.value })}
                  placeholder="12345678"
                  data-testid="num-ensayo-input"
                />
              </div>

              {/* Campo Nº Challenge - solo ANMAR */}
              {formData.destination === 'ANMAR' && (
                <div className="space-y-2">
                  <Label htmlFor="num_challenge">Nº Challenge</Label>
                  <Input
                    id="num_challenge"
                    value={formData.num_challenge}
                    onChange={(e) => setFormData({ ...formData, num_challenge: e.target.value })}
                    placeholder="Nº Challenge"
                    data-testid="num-challenge-input"
                  />
                </div>
              )}

              {/* Campo específico ANMAR */}
              {formData.destination === 'ANMAR' && (
                <div className="space-y-2">
                  <Label htmlFor="num_anmar">Nº ANMAR</Label>
                  <Input
                    id="num_anmar"
                    value={formData.num_anmar}
                    onChange={(e) => setFormData({ ...formData, num_anmar: e.target.value })}
                    placeholder="Código ANMAR"
                    data-testid="num-anmar-input"
                  />
                </div>
              )}
            </div>

            {/* Dates - Conditional based on destination */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Fecha Entrada - for all destinations */}
              <div className="space-y-2">
                <Label>F. Entrada GOYA</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="fecha_entrada-button"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dates.fecha_entrada ? (
                        format(dates.fecha_entrada, 'dd/MM/yyyy', { locale: es })
                      ) : (
                        <span className="text-gray-400">Seleccionar</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={dates.fecha_entrada}
                      onSelect={(date) => handleDateSelect('fecha_entrada', date)}
                      locale={es}
                    />
                    {(dates.fecha_entrada || formData.fecha_entrada) && (
                      <div className="px-3 pb-2">
                        <Button variant="ghost" size="sm" className="w-full text-red-500 hover:text-red-700 hover:bg-red-50 text-xs" onClick={() => clearDate('fecha_entrada')}>
                          Borrar fecha
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              {/* Fecha Envío - NOT for REGULATORY/ESTABILIDADES */}
              {!['REGULATORY', 'ESTABILIDADES'].includes(formData.destination) && (
                <div className="space-y-2">
                  <Label>F. Envío GOYA</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="fecha_envio-button"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dates.fecha_envio ? (
                          format(dates.fecha_envio, 'dd/MM/yyyy', { locale: es })
                        ) : (
                          <span className="text-gray-400">Seleccionar</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dates.fecha_envio}
                        onSelect={(date) => handleDateSelect('fecha_envio', date)}
                        locale={es}
                      />
                      {(dates.fecha_envio || formData.fecha_envio) && (
                        <div className="px-3 pb-2">
                          <Button variant="ghost" size="sm" className="w-full text-red-500 hover:text-red-700 hover:bg-red-50 text-xs" onClick={() => clearDate('fecha_envio')}>
                            Borrar fecha
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              
              {/* ANMAR-specific dates and fields */}
              {formData.destination === 'ANMAR' && (
                <>
                  {/* F. Ejecución ANMAR - Campo de texto libre */}
                  <div className="space-y-2">
                    <Label htmlFor="fecha_ejecucion">F. Ejecución ANMAR</Label>
                    <Input
                      id="fecha_ejecucion"
                      value={formData.fecha_ejecucion}
                      onChange={(e) =>
                        setFormData({ ...formData, fecha_ejecucion: e.target.value })
                      }
                      placeholder="Ej: Semana 12, Marzo 2026..."
                      data-testid="fecha-ejecucion-input"
                    />
                  </div>
                  
                  {/* F. Informe ANMAR - DatePicker */}
                  <div className="space-y-2">
                    <Label>F. Informe ANMAR</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="fecha_informe-button"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dates.fecha_informe ? (
                            format(dates.fecha_informe, 'dd/MM/yyyy', { locale: es })
                          ) : (
                            <span className="text-gray-400">Seleccionar</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={dates.fecha_informe}
                          onSelect={(date) => handleDateSelect('fecha_informe', date)}
                          locale={es}
                        />
                        {(dates.fecha_informe || formData.fecha_informe) && (
                          <div className="px-3 pb-2">
                            <Button variant="ghost" size="sm" className="w-full text-red-500 hover:text-red-700 hover:bg-red-50 text-xs" onClick={() => clearDate('fecha_informe')}>
                              Borrar fecha
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                </>
              )}

              {/* ESTABILIDADES: Salida Cámara section is rendered below */}
            </div>

            {/* ESTABILIDADES: Fechas Salida Cámara + F. Informe */}
            {formData.destination === 'ESTABILIDADES' && (
              <div className="mt-4">
                <SalidaCamaraSection
                  entries={formData.fechas_salida_camara}
                  onChange={(newEntries) => setFormData({ ...formData, fechas_salida_camara: newEntries })}
                  fechaEntradaGoya={formData.fecha_entrada}
                  fechaInformeEstabilidades={formData.fecha_informe_estabilidades}
                  onFechaInformeChange={(val) => setFormData({ ...formData, fecha_informe_estabilidades: val })}
                />
              </div>
            )}

            {/* ANMAR-specific Results and Common Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.destination === 'ANMAR' && (
                <div className="space-y-2">
                  <Label htmlFor="resultado_otros_ensayos">Resultado Otros Ensayos</Label>
                  <Textarea
                    id="resultado_otros_ensayos"
                    value={formData.resultado_otros_ensayos}
                    onChange={(e) =>
                      setFormData({ ...formData, resultado_otros_ensayos: e.target.value })
                    }
                    placeholder="Resultados de otros ensayos"
                    rows={3}
                    data-testid="resultado-ensayos-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Idioma (selección múltiple)</Label>
                <div className="grid grid-cols-3 gap-3">
                  {['Español', 'Inglés', 'Francés'].map((idioma) => (
                    <div
                      key={idioma}
                      className="flex items-center space-x-2 p-2 border rounded-lg hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        id={`idioma-${idioma}`}
                        checked={Array.isArray(formData.idioma) ? formData.idioma.includes(idioma) : false}
                        onChange={(e) => {
                          const currentIdiomas = Array.isArray(formData.idioma) ? formData.idioma : [];
                          const newIdiomas = e.target.checked
                            ? [...currentIdiomas, idioma]
                            : currentIdiomas.filter((i) => i !== idioma);
                          setFormData({ ...formData, idioma: newIdiomas });
                        }}
                        className="rounded"
                        data-testid={`idioma-checkbox-${idioma}`}
                      />
                      <label
                        htmlFor={`idioma-${idioma}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {idioma}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* GAIKER/IVAMI/REGULATORY specific fields */}
            {['GAIKER', 'IVAMI', 'REGULATORY'].includes(formData.destination) && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="coste_goya">Coste GOYA (€)</Label>
                    <Input
                      id="coste_goya"
                      type="number"
                      step="0.01"
                      value={formData.coste_goya}
                      onChange={(e) => setFormData({ ...formData, coste_goya: e.target.value })}
                      placeholder="0.00"
                      data-testid="coste-goya-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="num_presupuesto_goya">Nº Presupuesto GOYA</Label>
                    <Input
                      id="num_presupuesto_goya"
                      type="text"
                      value={formData.num_presupuesto_goya}
                      onChange={(e) => setFormData({ ...formData, num_presupuesto_goya: e.target.value })}
                      placeholder="Ej: PRES-2024-001"
                      data-testid="num-presupuesto-goya-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Campo condicional según destino */}
                  <div className="space-y-2">
                    <Label htmlFor="presupuesto_especifico">
                      {formData.destination === 'GAIKER' && 'Nº Presupuesto GAIKER'}
                      {formData.destination === 'IVAMI' && 'Nº Proforma IVAMI'}
                      {formData.destination === 'REGULATORY' && 'Nº Presupuesto'}
                    </Label>
                    <Input
                      id="presupuesto_especifico"
                      type="text"
                      value={
                        formData.destination === 'GAIKER' ? formData.num_presupuesto_gaiker :
                        formData.destination === 'IVAMI' ? formData.num_proforma_ivami :
                        ''
                      }
                      onChange={(e) => {
                        if (formData.destination === 'GAIKER') {
                          setFormData({ ...formData, num_presupuesto_gaiker: e.target.value });
                        } else if (formData.destination === 'IVAMI') {
                          setFormData({ ...formData, num_proforma_ivami: e.target.value });
                        }
                      }}
                      placeholder="Ej: PRES-2024-002"
                      data-testid="presupuesto-especifico-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>F. Envío Informe</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="fecha-envio-informe-button"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dates.fecha_envio_informe ? (
                            format(dates.fecha_envio_informe, 'dd/MM/yyyy', { locale: es })
                          ) : (
                            <span className="text-gray-400">Seleccionar</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={dates.fecha_envio_informe}
                          onSelect={(date) => handleDateSelect('fecha_envio_informe', date)}
                          locale={es}
                        />
                        {(dates.fecha_envio_informe || formData.fecha_envio_informe) && (
                          <div className="px-3 pb-2">
                            <Button variant="ghost" size="sm" className="w-full text-red-500 hover:text-red-700 hover:bg-red-50 text-xs" onClick={() => clearDate('fecha_envio_informe')}>
                              Borrar fecha
                            </Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </>
            )}

            {/* Payment System - ALL DESTINATIONS */}
            {['ANMAR', 'GAIKER', 'IVAMI', 'REGULATORY', 'ESTABILIDADES'].includes(formData.destination) && (
              <>
                <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-semibold text-gray-700">Hitos de Facturación</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="payment_total">Importe Total (€)</Label>
                      <Input
                        id="payment_total"
                        type="number"
                        step="0.01"
                        value={formData.payment_total}
                        onChange={(e) => setFormData({ ...formData, payment_total: e.target.value })}
                        placeholder="0.00"
                        data-testid="payment-total-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Tipo de Hito</Label>
                      <Select
                        value={formData.payment_type}
                        onValueChange={(value) => {
                          // Reset payments loaded flag when user manually changes payment type
                          setPaymentsLoadedFromEdit(false);
                          setFormData({ ...formData, payment_type: value });
                        }}
                        data-testid="payment-type-select"
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UNICO">Hito Único</SelectItem>
                          <SelectItem value="DOS_PAGOS">Dos Hitos</SelectItem>
                          <SelectItem value="TRES_PAGOS">Tres Hitos</SelectItem>
                          <SelectItem value="CUATRO_PAGOS">Cuatro Hitos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Payment Schedule */}
                  {payments.length > 0 && (
                    <div className="space-y-2">
                      <Label>Calendario de Hitos</Label>
                      <div className="space-y-2">
                        {payments.map((payment, index) => {
                          const baseYear = extractYearFromDate(formData.fecha_entrada) || String(new Date().getFullYear());
                          const yearOptions = generateYearOptions(baseYear, 5);
                          
                          return (
                          <div key={index} className="p-3 bg-white border rounded-lg">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={payment.paid}
                                  onChange={() => togglePaymentPaid(index)}
                                  className="rounded"
                                  data-testid={`payment-${index}-checkbox`}
                                />
                                <span className="font-medium">{formatPaymentName(payment.payment_number, getTotalPaymentsFromType(formData.payment_type))}</span>
                                {payment.paid && (
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                      <Label className="text-xs text-gray-500">Año:</Label>
                                      <Select
                                        value={payment.payment_year || baseYear}
                                        onValueChange={(value) => {
                                          const updated = [...payments];
                                          updated[index].payment_year = value;
                                          setPayments(updated);
                                        }}
                                      >
                                        <SelectTrigger className="w-20 h-8 text-xs">
                                          <SelectValue placeholder={baseYear} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {yearOptions.map((y) => (
                                            <SelectItem key={y} value={y}>
                                              {y}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Label className="text-xs text-gray-500">Mes:</Label>
                                      <Select
                                        value={payment.payment_month || ''}
                                        onValueChange={(value) => {
                                          const updated = [...payments];
                                          updated[index].payment_month = value;
                                          setPayments(updated);
                                        }}
                                      >
                                        <SelectTrigger className="w-32 h-8 text-xs">
                                          <SelectValue placeholder="Seleccionar" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {[
                                            { value: '01', label: 'Enero' },
                                            { value: '02', label: 'Febrero' },
                                            { value: '03', label: 'Marzo' },
                                            { value: '04', label: 'Abril' },
                                            { value: '05', label: 'Mayo' },
                                            { value: '06', label: 'Junio' },
                                            { value: '07', label: 'Julio' },
                                            { value: '08', label: 'Agosto' },
                                            { value: '09', label: 'Septiembre' },
                                            { value: '10', label: 'Octubre' },
                                            { value: '11', label: 'Noviembre' },
                                            { value: '12', label: 'Diciembre' },
                                          ].map((m) => (
                                            <SelectItem key={m.value} value={m.value}>
                                              {m.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Label className="text-xs text-gray-500">Facturación:</Label>
                                      <Select
                                        value={payment.billing_status || 'Pte facturar'}
                                        onValueChange={(value) => updatePaymentBillingStatus(index, value)}
                                      >
                                        <SelectTrigger className="w-32 h-8 text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="Pte facturar">Pte facturar</SelectItem>
                                          <SelectItem value="Facturado">Facturado</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <span className={`font-semibold ${payment.paid ? 'text-green-600' : 'text-gray-700'}`}>
                                €{formatPrice(payment.amount)}
                              </span>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="space-y-2 relative">
              <Label htmlFor="observaciones_incidencias">Observaciones / Incidencias</Label>
              <p className="text-xs text-gray-500">
                Usa <span className="font-semibold text-blue-600">@</span> para mencionar usuarios y enviar notificaciones
              </p>
              <Textarea
                ref={textareaRef}
                id="observaciones_incidencias"
                value={formData.observaciones_incidencias}
                onChange={handleObservacionesChange}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && showMentionSuggestions) {
                    setShowMentionSuggestions(false);
                  }
                }}
                placeholder="Registra observaciones, comentarios o cualquier incidencia. Usa @ para mencionar usuarios."
                rows={5}
                data-testid="observaciones-incidencias-input"
              />
              
              {/* Mentions dropdown */}
              <MentionSuggestions
                show={showMentionSuggestions}
                users={filteredUsers}
                onSelect={insertMention}
              />
            </div>

            {/* Estado (Multiselección) */}
            <div className="space-y-3">
              <Label>Estado <span className="text-xs text-gray-500 font-normal">(selección múltiple opcional)</span></Label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {estados.map((estado) => {
                  const isSelected = formData.status_id.includes(estado.id);
                  return (
                    <label
                      key={estado.id}
                      htmlFor={`estado-${estado.id}`}
                      className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-300' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        id={`estado-${estado.id}`}
                        checked={isSelected}
                        onChange={(e) => handleStatusSelect(estado.id, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        data-testid={`estado-checkbox-${estado.id}`}
                      />
                      <div className="text-sm font-medium cursor-pointer flex items-center gap-2 flex-1">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: estado.color }}
                        />
                        {estado.name}
                      </div>
                    </label>
                  );
                })}
              </div>
              {formData.status_id.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData({ ...formData, status_id: [] })}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Limpiar selección
                </Button>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-4 pt-6 border-t">
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  data-testid="submit-button"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {loading 
                    ? (isEditing ? 'Actualizando...' : 'Guardando...') 
                    : (isEditing ? 'Actualizar Muestra' : 'Guardar Muestra')}
                </Button>
                {!isEditing && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    disabled={loading}
                    data-testid="reset-button"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Limpiar Formulario
                  </Button>
                )}
              </div>

              {/* Clone Button */}
              {isEditing && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloneStudy}
                  disabled={loading}
                  className="w-full border-2 border-dashed border-blue-400 hover:bg-blue-50"
                  data-testid="clone-button"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Clonar Estudio (Crear Copias)
                </Button>
              )}
              {isEditing && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => returnTo === 'facturacion' ? navigate('/facturacion') : navigate('/dashboard', { state: { restoreFilters: dashboardFilters } })}
                  disabled={loading}
                  data-testid="cancel-button"
                >
                  Cancelar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </form>

      {/* Bulk Upload Section - Temporalmente desactivada
      {!isEditing && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet size={24} />
              Carga Masiva de Datos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">¿Cómo funciona?</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                <li>Descarga la plantilla de Excel para el destino que necesites</li>
                <li>Completa la plantilla con los datos de tus muestras</li>
                <li>Sube el archivo completado para importar todas las muestras de una vez</li>
              </ol>
              <p className="mt-3 text-xs text-blue-700">
                <strong>Nota:</strong> Solo los campos Destino, Cliente y Producto son obligatorios. El resto de campos se pueden completar después en el Dashboard.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">Paso 1: Descargar Plantilla</h4>
                <Button
                  onClick={() => setShowTemplateDialog(true)}
                  variant="outline"
                  className="w-full h-auto py-4 border-2 border-blue-500 hover:bg-blue-50"
                  data-testid="download-template-btn"
                >
                  <div className="flex flex-col items-center gap-2">
                    <Download size={32} className="text-blue-600" />
                    <span className="font-semibold">Descargar Plantilla Excel</span>
                    <span className="text-xs text-gray-500">Selecciona el destino</span>
                  </div>
                </Button>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">Paso 2: Subir Archivo Completado</h4>
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-green-500 transition-colors">
                    <input
                      id="bulk-upload-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <label
                      htmlFor="bulk-upload-input"
                      className="flex flex-col items-center gap-2 cursor-pointer"
                    >
                      <Upload size={32} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">
                        {selectedFile ? selectedFile.name : 'Seleccionar archivo Excel'}
                      </span>
                      <span className="text-xs text-gray-500">
                        Formatos: .xlsx, .xls
                      </span>
                    </label>
                  </div>

                  <Button
                    onClick={handleBulkUpload}
                    disabled={!selectedFile || uploadingFile}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    data-testid="upload-btn"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {uploadingFile ? 'Procesando...' : 'Subir y Procesar Archivo'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                <span>⚠️</span>
                Importante
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
                <li>Asegúrate de que los valores de Destino, Ensayo y Subcategoría existan en el sistema</li>
                <li>Los clientes deben estar previamente creados en Configuración</li>
                <li>Los nombres de Estado deben coincidir exactamente con los configurados</li>
                <li>Si hay errores en algunas filas, el sistema creará las muestras válidas e informará los errores</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
      */}

      {/* Clone Modal */}
      <CloneModal
        open={showCloneModal}
        onOpenChange={setShowCloneModal}
        quantity={cloneQuantity}
        onQuantityChange={setCloneQuantity}
        onConfirm={confirmClone}
        isLoading={cloningInProgress}
        currentNumGoya={formData.num_ensayo}
        previewNumbers={getPreviewNumGoya()}
      />

      {/* Clone After Create Modal */}
      <CloneModal
        open={showCloneAfterCreateModal}
        onOpenChange={setShowCloneAfterCreateModal}
        quantity={cloneQuantity}
        onQuantityChange={setCloneQuantity}
        onConfirm={handleCloneAfterCreate}
        onSkip={handleSkipCloneAfterCreate}
        isLoading={cloningInProgress}
        currentNumGoya={formData.num_ensayo}
        previewNumbers={getPreviewNumGoya()}
        variant="afterCreate"
      />

      {/* Duplicate Detection Dialog */}
      <DuplicateWarningDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        duplicateInfo={duplicateInfo}
        onConfirm={handleConfirmDuplicate}
        onCancel={handleCancelDuplicate}
      />

      {/* Bulk Upload Results Dialog */}
      <BulkUploadResultsDialog
        open={bulkUploadResults.open}
        onClose={() => setBulkUploadResults({ ...bulkUploadResults, open: false })}
        samplesCreated={bulkUploadResults.samplesCreated}
        errors={bulkUploadResults.errors}
      />

      {/* Template Download Dialog */}
      <TemplateDownloadDialog
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        onDownload={handleDownloadTemplate}
      />
    </div>
  );
};

export default EntradaDatos;
